import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiBriefcase, FiAlertCircle, FiCheck, FiPlus, FiTrash2, FiInfo, FiZap } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import {
  getBusinessTypes, getCoaTemplate, createCoaFromTemplate,
  BusiType, TemplateSection, TemplateAccount,
} from '../coaTemplateApi';
import './GLChartOfAccountsTab.css';

// =============================================
// Setup GL Accounts tab — from the business's type, show the merged starting-COA
// template as an editable/checkable staged list, then create the checked accounts into
// acctg_coa (building a header + child tree). Safe to re-run to add only missing accounts.
// =============================================
interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

interface BizEntity {
  owner_type: 'business' | 'client_business';
  business_id: number;
  name: string;
  industry_type_id: number | null;
}

// A staged, editable account row.
interface StagedRow {
  key: string;
  checked: boolean;
  account_kind: TemplateAccount['account_kind'];
  account_name: string;
  suggested_number: string;
  account_description: string;
  finsta_cate_id: number;
  normal_balance: 'Debit' | 'Credit';
  exists: boolean; // already present in the owner's COA
}
interface StagedSection {
  key: string;
  name: string;
  normal_balance: 'Debit' | 'Credit';
  rows: StagedRow[];
}

function resolveAccountOwnerId(users: Record<string, any>[]): number {
  const owner = users.find((u: any) => u.owner_id == null); // eslint-disable-line eqeqeq
  return owner ? Number(owner.user_id) : 0;
}
function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return Number(s.id || s.user_id) || null;
  } catch { return null; }
}

let rowSeq = 0;
const nextKey = () => `r${rowSeq++}`;

const SetupGlAccountsTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [types, setTypes] = useState<BusiType[]>([]);
  const [coa, setCoa] = useState<Record<string, any>[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const [selectedBizId, setSelectedBizId] = useState<number | null>(null);
  const [sections, setSections] = useState<StagedSection[]>([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resultMsg, setResultMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRows, bizRows, coaRows] = await Promise.all([
          loadTable('users').catch(() => getTableData('users')),
          loadTable('user_businesses').catch(() => getTableData('user_businesses')),
          loadTable('acctg_coa').catch(() => getTableData('acctg_coa')),
        ]);
        const ref = await getBusinessTypes();
        if (cancelled) return;
        setUsers(usersRows); setBusinesses(bizRows); setCoa(coaRows);
        setTypes(ref.types);
        setLoaded(true);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Could not load data.');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accountOwnerId = useMemo(() => resolveAccountOwnerId(users), [users]);

  const bizEntities = useMemo<BizEntity[]>(() => {
    if (!accountOwnerId) return [];
    const isClients = viewMode === 'clients';
    if (isClients && !selectedClientId) return [];
    return businesses
      .filter((b: any) => b.is_deleted != 1 && // eslint-disable-line eqeqeq
        (isClients
          ? (b.owner_id == selectedClientId && b.is_client_business == 1) // eslint-disable-line eqeqeq
          : (b.owner_id == accountOwnerId && b.is_client_business != 1))) // eslint-disable-line eqeqeq
      .map((b: any) => ({
        owner_type: (isClients ? 'client_business' : 'business') as BizEntity['owner_type'],
        business_id: Number(b.business_id),
        name: b.busi_legal_name || b.busi_operating_name || 'Business',
        industry_type_id: b.industry_type_id == null || b.industry_type_id === '' ? null : Number(b.industry_type_id),
      }));
  }, [businesses, accountOwnerId, viewMode, selectedClientId]);

  const selectedBiz = useMemo(
    () => bizEntities.find(b => b.business_id === selectedBizId) || null, [bizEntities, selectedBizId]);
  const selectedType = useMemo(
    () => selectedBiz && selectedBiz.industry_type_id
      ? types.find(t => t.busi_type_id === selectedBiz.industry_type_id) || null : null,
    [selectedBiz, types]);

  useEffect(() => {
    if (bizEntities.length === 1 && selectedBizId == null) setSelectedBizId(bizEntities[0].business_id);
  }, [bizEntities, selectedBizId]);

  // Existing accounts for the selected owner (for exists-detection / add-missing).
  const existing = useMemo(() => {
    const numbers = new Set<string>();
    const names = new Set<string>();
    if (!selectedBiz || !accountOwnerId) return { numbers, names };
    coa.filter((a: any) =>
      a.is_deleted != 1 && a.account_owner_id == accountOwnerId && // eslint-disable-line eqeqeq
      a.owner_type === selectedBiz.owner_type && a.owner_id == selectedBiz.business_id) // eslint-disable-line eqeqeq
      .forEach((a: any) => {
        if (a.account_number != null) numbers.add(String(a.account_number));
        if (a.account_name) names.add(String(a.account_name).toLowerCase());
      });
    return { numbers, names };
  }, [coa, selectedBiz, accountOwnerId]);

  // ---- Load the template for the selected business's type ----
  const loadTemplate = useCallback(async (typeId: number) => {
    setTemplateLoading(true);
    setError('');
    try {
      const resp = await getCoaTemplate(typeId);
      const staged: StagedSection[] = resp.sections.map((s: TemplateSection) => ({
        key: s.key,
        name: s.name,
        normal_balance: s.normal_balance,
        rows: s.accounts.map((a) => {
          const exists = existing.numbers.has(a.suggested_number) || existing.names.has(a.account_name.toLowerCase());
          return {
            key: nextKey(),
            checked: !exists, // pre-check everything not already in the COA
            account_kind: a.account_kind,
            account_name: a.account_name,
            suggested_number: a.suggested_number,
            account_description: a.account_description,
            finsta_cate_id: a.finsta_cate_id,
            normal_balance: a.normal_balance,
            exists,
          };
        }),
      }));
      setSections(staged);
    } catch (e: any) {
      setError(e?.message || 'Failed to load the starting accounts template.');
      setSections([]);
    } finally {
      setTemplateLoading(false);
    }
  }, [existing]);

  useEffect(() => {
    if (selectedType) loadTemplate(selectedType.busi_type_id);
    else setSections([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType?.busi_type_id]);

  // ---- Staged-list editing ----
  const markDirty = () => setFormDirty(true);
  const updateRow = (sk: string, rk: string, patch: Partial<StagedRow>) => {
    markDirty();
    setSections(prev => prev.map(s => s.key !== sk ? s : {
      ...s, rows: s.rows.map(r => r.key === rk ? { ...r, ...patch } : r),
    }));
  };
  const deleteRow = (sk: string, rk: string) => {
    markDirty();
    setSections(prev => prev.map(s => s.key !== sk ? s : { ...s, rows: s.rows.filter(r => r.key !== rk) }));
  };
  const addRow = (sk: string) => {
    markDirty();
    setSections(prev => prev.map(s => {
      if (s.key !== sk) return s;
      const kindByKey: Record<string, StagedRow['account_kind']> = {
        asset: 'asset', liability: 'liability', equity: 'equity',
        income: 'income', cogs: 'cogs', expense: 'expense',
      };
      // Default the new account's category to the section's first row (or a sensible fallback).
      const sample = s.rows[0];
      return {
        ...s,
        rows: [...s.rows, {
          key: nextKey(), checked: true,
          account_kind: kindByKey[s.key] || 'expense',
          account_name: '', suggested_number: '',
          account_description: '',
          finsta_cate_id: sample ? sample.finsta_cate_id : 14,
          normal_balance: s.normal_balance, exists: false,
        }],
      };
    }));
  };

  const checkedCount = useMemo(
    () => sections.reduce((n, s) => n + s.rows.filter(r => r.checked && r.account_name.trim()).length, 0),
    [sections]);
  const anyExisting = useMemo(() => existing.numbers.size > 0 || existing.names.size > 0, [existing]);

  // ---- Create ----
  const handleCreate = useCallback(async (): Promise<boolean> => {
    if (!selectedBiz || !selectedType) return false;
    const accounts = sections.flatMap(s => s.rows
      .filter(r => r.checked && r.account_name.trim())
      .map(r => ({
        account_kind: r.account_kind,
        suggested_number: r.suggested_number.trim() || '',
        account_name: r.account_name.trim(),
        account_description: r.account_description,
        finsta_cate_id: r.finsta_cate_id,
      })));
    if (accounts.length === 0) { setError('Check at least one account to create.'); return false; }

    setCreating(true); setError('');
    try {
      const res = await createCoaFromTemplate({
        owner_type: selectedBiz.owner_type,
        owner_id: selectedBiz.business_id,
        busi_type_id: selectedType.busi_type_id,
        accounts,
      });
      setFormDirty(false);
      setResultMsg(`Created ${res.accounts_created} account${res.accounts_created === 1 ? '' : 's'}` +
        (res.headers_created ? ` under ${res.headers_created} new section header${res.headers_created === 1 ? '' : 's'}` : '') +
        (res.skipped ? ` · skipped ${res.skipped} already-present` : '') + '.');
      setTimeout(() => setResultMsg(''), 5000);

      // Refresh the owner's COA so exists-detection updates + re-run adds only missing.
      const coaRows = await loadTable('acctg_coa').catch(() => getTableData('acctg_coa'));
      setCoa(coaRows);
      await loadTemplate(selectedType.busi_type_id);

      const uid = currentUserId();
      if (uid && uid !== accountOwnerId && accountOwnerId) {
        const actor = users.find((u: any) => Number(u.user_id) === uid);
        const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : 'A user';
        addNotification({
          account_owner_id: accountOwnerId, type: 'coa_from_template',
          title: 'Starting accounts created',
          message: `${actorName} created ${res.accounts_created} starting account(s) for "${selectedBiz.name}".`,
          actor_user_id: uid, actor_name: actorName,
        });
      }
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to create accounts.');
      return false;
    } finally {
      setCreating(false);
    }
  }, [selectedBiz, selectedType, sections, accountOwnerId, users, loadTemplate]);

  useEffect(() => {
    registerSaveHandler(() => handleCreate());
    return () => registerSaveHandler(null);
  }, [handleCreate]);

  // ---- Render ----
  if (!loaded) return <div className="coa-tab"><div className="coa-empty"><p>Loading…</p></div></div>;
  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  return (
    <div className="coa-tab">
      <div className="coa-info-banner">
        <FiZap size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
        Start from a ready-made Chart of Accounts for your business type. Edit names/numbers, uncheck what
        you don't need, then <strong>Create</strong>. You can add more or refine later in <strong>Chart of Accounts</strong>.
      </div>

      {error && <div className="coa-field-error" style={{ marginBottom: 12 }}><FiAlertCircle size={14} /> {error}</div>}

      {bizEntities.length === 0 ? (
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiBriefcase size={40} /></div>
          <p>No businesses found. Add a business in <strong>Family &amp; Businesses</strong> first.</p>
        </div>
      ) : (
        <>
          {bizEntities.length > 1 && (
            <div className="coa-form-card" style={{ marginBottom: 12 }}>
              <div className="coa-field-group">
                <label className="coa-field-label">Business</label>
                <SearchableDropdown
                  options={bizEntities.map(b => ({ value: String(b.business_id), label: b.name } as DropdownOption))}
                  value={selectedBizId ? String(selectedBizId) : ''}
                  onChange={(v) => setSelectedBizId(v ? Number(v) : null)}
                  placeholder="-- Select a business --"
                />
              </div>
            </div>
          )}

          {selectedBiz && !selectedBiz.industry_type_id && (
            <div className="coa-empty">
              <div className="coa-empty-icon"><FiInfo size={40} /></div>
              <p>Set this business's type first in the <strong>Type of Business</strong> tab to see suggested accounts.</p>
            </div>
          )}

          {selectedBiz && selectedBiz.industry_type_id && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 2px 12px', flexWrap: 'wrap' }}>
                <span className="coa-dropdown-badge">{selectedType?.type_name || 'Business type'}</span>
                {anyExisting && (
                  <span className="coa-field-hint" style={{ color: 'var(--gray-500)' }}>
                    This business already has some accounts — already-present ones are unchecked.
                  </span>
                )}
              </div>

              {templateLoading ? (
                <div className="coa-empty"><p>Loading suggested accounts…</p></div>
              ) : (
                <>
                  {sections.map(section => (
                    <div key={section.key} className="coa-form-card" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <h4 style={{ margin: 0 }}>{section.name}
                          <span className="coa-field-hint" style={{ marginLeft: 8 }}>({section.normal_balance})</span>
                        </h4>
                        <button className="coa-btn-secondary" style={{ fontSize: 13, padding: '4px 10px' }}
                          onClick={() => addRow(section.key)}>
                          <FiPlus size={13} /> Add account
                        </button>
                      </div>

                      {section.rows.length === 0 && (
                        <div className="coa-field-hint" style={{ padding: '4px 0' }}>No accounts. Use “Add account”.</div>
                      )}

                      {section.rows.map(row => (
                        <div key={row.key} className="coa-template-row"
                          style={{
                            display: 'grid', gridTemplateColumns: '28px 90px 1fr 32px',
                            gap: 8, alignItems: 'center', padding: '4px 0',
                            opacity: row.exists && !row.checked ? 0.6 : 1,
                          }}>
                          <input type="checkbox" checked={row.checked}
                            onChange={e => updateRow(section.key, row.key, { checked: e.target.checked })} />
                          <input className="coa-search-input" type="text" value={row.suggested_number}
                            placeholder="No."
                            onChange={e => updateRow(section.key, row.key, { suggested_number: e.target.value })} />
                          <input className="coa-search-input" type="text" value={row.account_name}
                            placeholder="Account name"
                            onChange={e => updateRow(section.key, row.key, { account_name: e.target.value })} />
                          <button className="coa-btn-icon danger" title="Remove from list"
                            onClick={() => deleteRow(section.key, row.key)} style={{ width: 28, height: 28 }}>
                            <FiTrash2 size={13} />
                          </button>
                          {row.exists && (
                            <span className="coa-field-hint" style={{ gridColumn: '3 / 4', marginTop: -2, color: 'var(--gray-400)' }}>
                              Already in your Chart of Accounts
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  {sections.length > 0 && (
                    <div className="coa-action-bar" style={{ position: 'sticky', bottom: 0 }}>
                      <button className="coa-btn-primary" disabled={checkedCount === 0 || creating}
                        onClick={handleCreate}>
                        <FiCheck size={15} /> {creating ? 'Creating…' :
                          `Create ${checkedCount} account${checkedCount === 1 ? '' : 's'}`}
                      </button>
                      {resultMsg && <span className="coa-saved-msg" style={{ marginLeft: 12 }}>{resultMsg}</span>}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SetupGlAccountsTab;
